import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import styles from './ShimmerTabLabel.module.css';

const SHIMMER_INITIAL_DELAY_SECONDS = 0;
const SHIMMER_INITIAL_POSITION = '140% center';
const SHIMMER_END_POSITION = '-40% center';
const SHIMMER_RESTART_DELAY_MS = 3000;
const SHIMMER_SPREAD_MULTIPLIER = 4;
const SHIMMER_SWEEP_DURATION_SECONDS = 2;
const SHIMMER_REPEAT_COUNT = 0;

type ShimmerTabLabelProps = {
  active?: boolean;
  children: ReactNode;
  inheritTabColor?: boolean;
  searchCycleKey?: string;
};

export function ShimmerTabLabel({
  children,
  active = true,
  inheritTabColor = false,
  searchCycleKey,
}: ShimmerTabLabelProps) {
  const [cycleIndex, setCycleIndex] = useState(0);
  const [isBetweenShimmerCycles, setIsBetweenShimmerCycles] = useState(false);
  const label = typeof children === 'string' ? children : String(children);
  const shimmerSpread = label.length * SHIMMER_SPREAD_MULTIPLIER;
  const shimmerBackgroundSize = 250 + Math.floor(shimmerSpread / 2);
  const wrapperClassName = clsx(styles.restingLabel, {
    [styles.inheritTabColor]: inheritTabColor,
  });
  const shimmerClassName = clsx(styles.shimmerLabel, {
    [styles.inheritTabColor]: inheritTabColor,
  });

  useEffect(() => {
    setCycleIndex(0);
    setIsBetweenShimmerCycles(false);
  }, [active, searchCycleKey]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (active && isBetweenShimmerCycles) {
      timeoutId = setTimeout(() => {
        setCycleIndex((currentCycleIndex) => currentCycleIndex + 1);
        setIsBetweenShimmerCycles(false);
      }, SHIMMER_RESTART_DELAY_MS);
    }

    return () => clearTimeout(timeoutId);
  }, [active, isBetweenShimmerCycles]);

  if (!active || isBetweenShimmerCycles) {
    return <span className={wrapperClassName}>{children}</span>;
  }

  return (
    <motion.span
      key={`${searchCycleKey ?? ''}:${cycleIndex}`}
      animate={{ backgroundPosition: SHIMMER_END_POSITION }}
      className={shimmerClassName}
      data-chromatic="ignore"
      initial={{ backgroundPosition: SHIMMER_INITIAL_POSITION }}
      onAnimationComplete={() => setIsBetweenShimmerCycles(true)}
      style={
        {
          '--bg-size': `${shimmerBackgroundSize}%`,
          '--spread': `${shimmerSpread}px`,
        } as CSSProperties
      }
      transition={{
        delay: SHIMMER_INITIAL_DELAY_SECONDS,
        duration: SHIMMER_SWEEP_DURATION_SECONDS,
        ease: 'linear',
        repeat: SHIMMER_REPEAT_COUNT,
      }}
    >
      {children}
    </motion.span>
  );
}
